import { Select, Result, Empty, Button, Space, Form, notification, Divider } from "antd";
import { EXOSCALE_ZONES } from "../enums/enums";
import { useEffect, useState } from "react";
import CloudIcon from "./CloudIcon";
import { trackSpecCloudAccountInit } from "../backend/tracking/user-settings";
import { ExoscaleZoneType, IEnvDetails } from "../types";
import { RequiredMark } from "antd/es/form/Form";
import { getCloudService, getEnvironmentService } from "../backend";
import { useParams } from "react-router";
import { UpdateStageRequestExoscaleZoneEnum } from "@microtica/ms-engine-sdk";
import { canUpdateComponent } from "../utils/environment";
import DeploymentStatusTag from "./DeploymentStatusTag";
import ConnectExoscaleAccountModal from "./settings/ConnectExoscaleAccountModal";

interface ChooseExoscaleAccountFormProps {
    env: IEnvDetails;
    onSelect?: ({ exoscaleAccountId, exoscaleZone }: { exoscaleAccountId: string, exoscaleZone: string }) => void;
    onCancel?: () => void;
}
const ChooseExoscaleAccountForm = ({
    env,
    onSelect,
    onCancel
}: ChooseExoscaleAccountFormProps) => {

    console.log("ENV", env);

    const { projectId } = useParams() as { projectId: string; }
    const [accountForm] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [cloudAccounts, setCloudAccounts] = useState<{ id: string; name: string }[]>([]);
    const [connectExoscaleAccountModalVisible, setConnectExoscaleAccountModalVisible] = useState(false);
    const [loadingUpdateAccount, setLoadingUpdateAccount] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        const canUpdate = canUpdateComponent(env.status);

        const { data: { exoscaleAccounts } } = await getCloudService().getExoscaleAccounts(projectId);
        setCloudAccounts(
            exoscaleAccounts.map(acc => ({ id: acc.id!, name: acc.accountName! }))
        );
        setCanUpdate(canUpdate || (!env.exoscaleAccountId && !!exoscaleAccounts.length));
    }

    async function handleUpdateAccount(values: {
        exoscaleAccountId: string;
        exoscaleZone: ExoscaleZoneType;
    }) {
        try {
            setLoadingUpdateAccount(true);

            const updatedAccount = {
                exoscaleAccountId: values.exoscaleAccountId,
                exoscaleZone: UpdateStageRequestExoscaleZoneEnum[values.exoscaleZone]
            };
            await getEnvironmentService().updateStage(
                env.id,
                projectId,
                updatedAccount
            );

            onSelect?.(updatedAccount);
            env.onChange?.();

            notification.success({
                message: "Cloud account updated",
                description: "The Cloud account for this environment has been updated successfully",
            });
        } catch (error: any) {
            console.log("ERROR", error);
            notification.error({
                message: "Cloud account can not be updated",
                description: error?.response?.data ? error.response.data.message : "We encountered an error while updating this environment. Please contact our support team on support@microtica.com or write on our Discord community channel."
            });
        } finally {
            setLoadingUpdateAccount(false);
        }
    }

    return (
        !cloudAccounts.length ?
            <>
                <Result
                    icon={Empty.PRESENTED_IMAGE_SIMPLE}
                    subTitle={
                        <>
                            <div>
                                It looks like you haven't connected any cloud accounts yet!
                            </div>
                            <div>
                                Connect a cloud account to deploy your infrastructure.
                            </div>
                        </>
                    }
                    extra={
                        <Button onClick={() => {
                            setConnectExoscaleAccountModalVisible(true);
                            trackSpecCloudAccountInit("exoscale");
                        }}>
                            <Space>
                                <CloudIcon provider={env.cloudProvider} /> Connect Exoscale Account
                            </Space>
                        </Button>
                    }
                />
                {
                    // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'connectExoscaleAccountModalVisible'
                    // We have a process of periodic API calls which remain active even if when the modal closes
                    connectExoscaleAccountModalVisible &&
                    <ConnectExoscaleAccountModal
                        visible={connectExoscaleAccountModalVisible}
                        onOk={() => {
                            loadData();
                            setConnectExoscaleAccountModalVisible(false);
                        }}
                        onCancel={() => setConnectExoscaleAccountModalVisible(false)}
                    />
                }
            </> :
            <Form
                form={accountForm}
                layout="vertical"
                initialValues={{
                    exoscaleAccountId: env.exoscaleAccountId,
                    exoscaleZone: env.exoscaleZone && EXOSCALE_ZONES.find(z => z.value === env.exoscaleZone)!.id
                }}
                requiredMark={requiredMark}
                onFinish={handleUpdateAccount}
            >
                <Form.Item
                    name="exoscaleAccountId"
                    label="Cloud account"
                    required
                    rules={[{ required: true, message: 'Please select cloud account!' }]}
                >
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Exoscale account"
                        disabled={!canUpdate}
                        filterOption={(input, option: any) => {
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }}
                        filterSort={(optionA, optionB) => {
                            return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                        }}
                    >
                        {
                            cloudAccounts.map(acc => (
                                <Select.Option value={acc.id} key={acc.id}>
                                    {acc.name}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Form.Item
                    name="exoscaleZone"
                    label="Exoscale zone"
                    required
                    rules={[{ required: true, message: 'Please select cloud zone!' }]}
                >
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Select zone"
                        disabled={!canUpdate}
                        filterOption={(input, option: any) => {
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }}
                        filterSort={(optionA, optionB) => {
                            return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                        }}
                    >
                        {
                            EXOSCALE_ZONES.map(zone => (
                                <Select.Option value={zone.id} key={zone.id}>
                                    {zone.name}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </Form.Item>
                <Divider />
                <div className="flex-justify-space-between flex-align-center">
                    <div>
                        {
                            canUpdate ? undefined :
                                <>
                                    Account cannot be updated <DeploymentStatusTag status={env.status} />
                                </>
                        }
                    </div>
                    <Space>
                        {
                            onCancel ? <Button onClick={onCancel}>
                                Close
                            </Button> : undefined
                        }
                        <Button htmlType="submit" disabled={!canUpdate} loading={loadingUpdateAccount} type="primary">
                            Save
                        </Button>
                    </Space>
                </div>
            </Form>
    );
}

export default ChooseExoscaleAccountForm;

